import { observer } from "mobx-react-lite";
import { useEffect, useRef } from "react";
import { t } from "i18next";
import styled from "styled-components";
import type { NodeProps } from "@xyflow/react";
import { Text, Icon } from "@smartsuite/react-ui/lib";
import { Colors, GrayColors } from "@smartsuite/types";
import { useRootStore } from "../../../stores/hooks";
import { locateEnclosingContainer } from "../../../stores/automation/utils/action-group-walk";
import {
  StyledHeaderWrapper,
  StyledLabel,
  StyledDeleteButton,
} from "../../ActionGroup/styles";
import { useFlowCanvasHandlers } from "./context";
import { HiddenHandles } from "./handles";
import type { FlowNodeData } from "../graph/types";

/* The loop container chrome for the canvas: unlike the legacy ActionGroup
   container (list-mode geometry: negative margins, 420px cap, inline
   children) the RF group node fills the box the layout reserves for it and
   its children are separate nodes positioned inside. Only the header renders
   here, pinned to the top; the body stays empty. Class rules mirror
   ActionGroup/styles.ts so the header reads identically. */
const StyledLoopContainer = styled.div<{
  $isActive: boolean;
  $isCollapsed: boolean;
}>`
  position: relative;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  border-radius: 8px;
  border: 1px solid var(--solution-primary-color);
  opacity: ${({ $isActive }) => ($isActive ? "1" : "0.9")};

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background-color: var(--solution-primary-color);
    opacity: 0.03;
    pointer-events: none;
    border-radius: 8px;
  }

  /* Expanded points the chevron down (rotate 180 of the arrow-up icon);
     collapsing spins it up. Transition respects reduced-motion below. */
  .action-group-arrow {
    transform: rotate(
      ${({ $isCollapsed }) => ($isCollapsed ? "0deg" : "180deg")}
    );
    transition: transform 150ms ease;
    color: var(--solution-primary-color);
  }

  @media (prefers-reduced-motion: reduce) {
    .action-group-arrow {
      transition: none;
    }
  }

  .action-group-title {
    opacity: 0.7;
    display: flex;
    gap: 5px;
  }

  .action-group-name,
  .action-group-name-empty {
    display: inline-block;
  }

  .action-group-name-empty {
    margin-left: 10px;
  }
`;

const StyledLoopHeader = styled.div<{ $isCollapsed: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  width: 100%;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  cursor: pointer;

  &::before {
    content: "";
    position: absolute;
    left: 16px;
    right: 16px;
    bottom: 0;
    background-color: var(--solution-primary-color);
    height: 1px;
    opacity: 0.18;
    display: ${({ $isCollapsed }) => ($isCollapsed ? "none" : "block")};
  }
`;

/**
 * React Flow group node for a loop. Reads the loop's live scope (name/error/
 * empty state) via `looping.scopeFor`; delete mirrors `NestedLoopGroup.tsx`'s
 * confirm-on-content flow (works for the root loop and nested loops alike via
 * `deleteNestedLoop`). The collapse chevron toggles `flowCanvas.collapsedLoopIds`,
 * which `use-flow-graph` reads to omit the loop's children from the graph.
 */
export const LoopGroupNode = observer<NodeProps>(({ data }) => {
  const { instanceId } = data as Extract<FlowNodeData, { kind: "loopGroup" }>;
  const store = useRootStore();
  const handlers = useFlowCanvasHandlers();
  const candidate = store.automations.candidate;
  const scope = candidate.looping.scopeFor(instanceId);

  // The header is absolutely positioned, so it never grows the loop node's
  // own box — the layout engine sizes that from `looping.scopeFor`. Report the
  // header's real (wrapped) height to the store so the layout reserves room
  // for a multi-line group name instead of clipping it (BUG-21207).
  const headerRef = useRef<HTMLDivElement>(null);
  const hasScope = scope !== undefined;
  useEffect(() => {
    const el = headerRef.current;
    if (el === null) return;
    const measure = (): void => {
      store.flowCanvas.setLoopHeaderHeight(instanceId, el.offsetHeight);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [instanceId, store, hasScope]);

  if (scope === undefined) return null;

  const isCollapsed = store.flowCanvas.isLoopCollapsed(instanceId);
  const defaultName = scope.getDefaultGroupName();
  const name =
    scope.loopName ||
    (defaultName.isSourceSet
      ? defaultName.groupName
      : t("general.repeat-for-each-in"));
  // Output descriptions load asynchronously on open; until then the source
  // can't be resolved, so hold back the empty/error markers (BUG-21925).
  const isDescriptionsLoaded =
    store.automations.isAllDynamicDescriptionsLoaded();
  const isEmpty = isDescriptionsLoaded && !defaultName.isSourceSet;
  const isError = isDescriptionsLoaded && !scope.isConfiguredSourceValid;

  const isRootLoop = candidate.self.actionGroups.some(
    (group) =>
      group.instanceId === instanceId && group.config.oneofKind === "loop",
  );
  const isActive =
    (handlers.activeItem?.type === "nested-loop" &&
      handlers.activeItem.instanceId === instanceId) ||
    (isRootLoop && handlers.activeItem?.type === "looping");

  const handleToggleCollapsed = (): void => {
    store.flowCanvas.toggleLoopCollapsed(instanceId);
  };

  // legacy Looping/index.tsx: select the last remaining root action, or the
  // trigger when none are left. Also the fallback when a nested loop's
  // enclosing container can't be resolved.
  const selectRootFallback = (): void => {
    const rootActions = candidate.groupActions;
    const nextActionHash = rootActions[rootActions.length - 1]?.hash ?? "";
    nextActionHash
      ? handlers.onClickAction(nextActionHash)
      : handlers.onClickTrigger();
  };

  const handleDelete = (e: React.MouseEvent): void => {
    e.stopPropagation();
    const performDelete = (): void => {
      // Resolved BEFORE deleteNestedLoop mutates the tree: the group (and
      // its position in the tree) is gone once the delete below succeeds.
      const enclosingContainer = isRootLoop
        ? undefined
        : locateEnclosingContainer(candidate.self.actionGroups, instanceId);
      if (!candidate.branches.deleteNestedLoop(instanceId)) return;
      if (isRootLoop) {
        selectRootFallback();
        return;
      }
      if (enclosingContainer !== undefined && enclosingContainer !== "root") {
        if (enclosingContainer.kind === "loop") {
          // A root-level parent loop is selected via the "looping" section
          // (its node highlights on type "looping", not "nested-loop"); a
          // deeper parent loop uses the nested-loop selection.
          const parentIsRootLoop = candidate.self.actionGroups.some(
            (group) =>
              group.instanceId === enclosingContainer.instanceId &&
              group.config.oneofKind === "loop",
          );
          if (parentIsRootLoop) {
            handlers.onSelectLooping("");
          } else {
            handlers.onSelectNestedLoop(enclosingContainer.instanceId);
          }
          return;
        }
        if (enclosingContainer.kind === "branch") {
          handlers.onSelectBranch(
            enclosingContainer.instanceId,
            enclosingContainer.branchHash,
          );
          return;
        }
      }
      // "root" or unresolved: deleting a loop whose parent couldn't be
      // located falls back to the same root selection as a root-loop delete.
      selectRootFallback();
    };
    if (!candidate.branches.loopHasContent(instanceId)) {
      performDelete();
      return;
    }
    store.modal.openConfirmModal({
      type: "warning",
      title: t("confirm-modal.are-you-sure"),
      description: `${t("branches.delete-loop-confirm")}. ${t(
        "confirm-modal.cannot-undone-action",
      )}.`,
      positiveButtonText: t("branches.yes-delete-loop"),
      negativeButtonText: t("confirm-modal.no-cancel"),
      onClose: () => store.modal.closeConfirmModal(),
      onConfirm: () => {
        performDelete();
        store.modal.closeConfirmModal();
      },
    });
  };

  return (
    <>
      <HiddenHandles />
      <StyledLoopContainer
        data-testid={`loop-group-node-${instanceId}`}
        $isActive={isActive}
        $isCollapsed={isCollapsed}
        onClick={() =>
          isRootLoop
            ? handlers.onSelectLooping("")
            : handlers.onSelectNestedLoop(instanceId)
        }
      >
        <StyledLoopHeader
          ref={headerRef}
          data-testid={`loop-group-collapse-${instanceId}`}
          data-collapsed={String(isCollapsed)}
          $isCollapsed={isCollapsed}
          onClick={handleToggleCollapsed}
        >
          <Icon
            className="action-group-arrow"
            name="arrow-up"
            size="M"
            color="currentColor"
          />
          <StyledHeaderWrapper>
            <Text size="xs" weight="medium" className="action-group-title">
              {t("general.looping")}
              {isError && <Icon name="important-lined" color={Colors.bRed} />}
            </Text>
            <Text className="action-group-name" size="l" weight="semibold">
              {name}{" "}
              {isEmpty && (
                <StyledLabel className="action-group-name-empty">
                  {t("general.action-group-no-list")}
                </StyledLabel>
              )}
            </Text>
          </StyledHeaderWrapper>
          <StyledDeleteButton
            data-testid={`loop-group-delete-${instanceId}`}
            onClick={handleDelete}
          >
            <Icon name="trash-thin" size="S" color={GrayColors["G-8"]} />
          </StyledDeleteButton>
        </StyledLoopHeader>
      </StyledLoopContainer>
    </>
  );
});
LoopGroupNode.displayName = "LoopGroupNode";
